<template>
    <div>
        <h1 class="text-h4 mb-6">Medlemsdata</h1>
        <v-card v-if="medlemsdata" class="mb-6">
            <v-card-text>
                <v-row>
                    <v-col cols="12" md="6">
                        <v-list>
                            <v-list-item>
                                <v-list-item-content>
                                    <v-list-item-title class="text-subtitle-1 font-weight-bold">Medlemsnummer</v-list-item-title>
                                    <v-list-item-subtitle>{{ medlemsdata.id }}</v-list-item-subtitle>
                                </v-list-item-content>
                            </v-list-item>
                            <v-list-item>
                                <v-list-item-content>
                                    <v-list-item-title class="text-subtitle-1 font-weight-bold">Navn</v-list-item-title>
                                    <v-list-item-subtitle>{{ medlemsdata.navn }}</v-list-item-subtitle>
                                </v-list-item-content>
                            </v-list-item>
                            <v-list-item>
                                <v-list-item-content>
                                    <v-list-item-title class="text-subtitle-1 font-weight-bold">Email</v-list-item-title>
                                    <v-list-item-subtitle>{{ medlemsdata.email }}</v-list-item-subtitle>
                                </v-list-item-content>
                            </v-list-item>
                            <v-list-item>
                                <v-list-item-content>
                                    <v-list-item-title class="text-subtitle-1 font-weight-bold">Telefon</v-list-item-title>
                                    <v-list-item-subtitle>{{ medlemsdata.telefon }}</v-list-item-subtitle>
                                </v-list-item-content>
                            </v-list-item>
                            <v-list-item>
                                <v-list-item-content>
                                    <v-list-item-title class="text-subtitle-1 font-weight-bold">MobilePay Aftale</v-list-item-title>
                                    <v-list-item-subtitle>{{ medlemsdata.mobilePayAgreementID || 'Ingen' }}</v-list-item-subtitle>
                                </v-list-item-content>
                            </v-list-item>
                            <v-list-item>
                                <v-list-item-content>
                                    <v-list-item-title class="text-subtitle-1 font-weight-bold">MobilePay Aftale status</v-list-item-title>
                                    <v-list-item-subtitle>
                                        <v-chip :color="agreementStatusColor(medlemsdata.mobilePayAgreementStatus)" small>
                                            {{ agreementStatusLabel(medlemsdata.mobilePayAgreementStatus) }}
                                        </v-chip>
                                    </v-list-item-subtitle>
                                </v-list-item-content>
                            </v-list-item>
                        </v-list>
                    </v-col>
                    <v-col cols="12" md="6">
                        <v-list>
                            <v-list-item>
                                <v-list-item-content>
                                    <v-list-item-title class="text-subtitle-1 font-weight-bold">Adresse</v-list-item-title>
                                    <v-list-item-subtitle>{{ medlemsdata.adresse }}</v-list-item-subtitle>
                                </v-list-item-content>
                            </v-list-item>
                            <v-list-item>
                                <v-list-item-content>
                                    <v-list-item-title class="text-subtitle-1 font-weight-bold">Postnummer og by</v-list-item-title>
                                    <v-list-item-subtitle>{{ medlemsdata.postnummer }} {{ medlemsdata.by }}</v-list-item-subtitle>
                                </v-list-item-content>
                            </v-list-item>
                            <v-list-item>
                                <v-list-item-content>
                                    <v-list-item-title class="text-subtitle-1 font-weight-bold">Medlem siden</v-list-item-title>
                                    <v-list-item-subtitle>{{ formatDate(medlemsdata.createdAt) }}</v-list-item-subtitle>
                                </v-list-item-content>
                            </v-list-item>
                            <v-list-item>
                                <v-list-item-content>
                                    <v-list-item-title class="text-subtitle-1 font-weight-bold">Status</v-list-item-title>
                                    <v-list-item-subtitle>
                                        <v-chip :color="medlemsdata.active ? 'success' : 'error'" small>
                                            {{ medlemsdata.active ? 'Aktiv' : 'Inaktiv' }}
                                        </v-chip>
                                    </v-list-item-subtitle>
                                </v-list-item-content>
                            </v-list-item>
                            <v-list-item>
                                <v-list-item-content>
                                    <v-list-item-title class="text-subtitle-1 font-weight-bold">Nyhedsbrev</v-list-item-title>
                                    <v-list-item-subtitle>
                                        <v-chip :color="medlemsdata.nyhedsbrev ? 'success' : 'error'" small>
                                            {{ medlemsdata.nyhedsbrev ? 'Aktiv' : 'Inaktiv' }}
                                        </v-chip>
                                    </v-list-item-subtitle>
                                </v-list-item-content>
                            </v-list-item>
                        </v-list>
                    </v-col>
                </v-row>
            </v-card-text>
            <v-card-actions v-if="medlemsdata.active">
                <v-btn
                    color="error"
                    outlined
                    :loading="deactivating"
                    @click="confirmDialog = true"
                >
                    Deaktiver medlem
                </v-btn>
            </v-card-actions>
        </v-card>
        <v-alert
            v-if="deactivateMessage"
            :type="deactivateMessageType"
            class="mb-6"
            dismissible
            @input="deactivateMessage = null"
        >
            {{ deactivateMessage }}
        </v-alert>
        <v-dialog v-if="medlemsdata" v-model="confirmDialog" max-width="500">
            <v-card>
                <v-card-title>Deaktiver medlem?</v-card-title>
                <v-card-text>
                    Er du sikker på, at du vil deaktivere {{ medlemsdata.navn }}?
                </v-card-text>
                <v-card-actions>
                    <v-spacer></v-spacer>
                    <v-btn text @click="confirmDialog = false">Annuller</v-btn>
                    <v-btn color="error" :loading="deactivating" @click="deactivateMember">Deaktiver</v-btn>
                </v-card-actions>
            </v-card>
        </v-dialog>
        <template v-if="!medlemsdata">
            <v-alert
                v-if="error"
                type="error"
            >
                {{ error }}
            </v-alert>
            <v-progress-circular
                v-else
                indeterminate
                color="primary"
            ></v-progress-circular>
        </template>
    </div>
</template>

<script>
import MedlemsService from '@/services/MedlemsService'
import moment from 'moment'

export default {
  name: 'MedlemsData',
  props: {
    memberId: {
      type: [String, Number],
      required: true
    }
  },
  data () {
    return {
      medlemsdata: null,
      error: null,
      confirmDialog: false,
      deactivating: false,
      deactivateMessage: null,
      deactivateMessageType: 'success'
    }
  },
  watch: {
    memberId: {
      immediate: true,
      handler () {
        this.fetchMemberData()
      }
    }
  },
  methods: {
    async fetchMemberData () {
      try {
        this.error = null
        const response = await MedlemsService.getMedlemsdata(this.memberId)
        this.medlemsdata = response.data
      } catch (error) {
        this.error = 'Kunne ikke hente medlemsdata'
        console.error('Error fetching member data:', error)
      }
    },
    formatDate (date) {
      return moment(date).format('DD/MM/YYYY')
    },
    agreementStatusLabel (status) {
      if (!status) return 'Ingen aftale'
      if (status === 'ACTIVE') return 'Aktiv'
      if (status === 'STOPPED') return 'Stoppet'
      return status
    },
    agreementStatusColor (status) {
      if (status === 'ACTIVE') return 'success'
      if (status === 'STOPPED') return 'error'
      return 'grey'
    },
    async deactivateMember () {
      this.deactivating = true
      this.deactivateMessage = null
      try {
        const response = await MedlemsService.deactivateMedlem(this.memberId)
        this.medlemsdata = response.data
        this.confirmDialog = false
        this.deactivateMessage = 'Medlem deaktiveret'
        this.deactivateMessageType = 'success'
      } catch (error) {
        this.deactivateMessage = error.response?.data?.error || 'Kunne ikke deaktivere medlem'
        this.deactivateMessageType = 'error'
        console.error('Error deactivating member:', error)
      } finally {
        this.deactivating = false
      }
    }
  }
}
</script>

<style scoped>
.v-list-item {
    padding-left: 0;
}
</style>
